import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Spinner, fmtKg } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Plus, Users, Package, Search } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

const STATUS_TONE = {
  bruto: "warn",
  em_beneficiamento: "info",
  beneficiada: "success",
};

const STATUS_LABEL = {
  bruto: "Aguardando",
  em_beneficiamento: "Em beneficiamento",
  beneficiada: "Finalizada",
};

export default function TerceirosNFsList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { page: 1, page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/terceiros/nfs", { params });
      setItems(data.items || data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const totais = items.reduce(
    (a, i) => ({
      qtd: a.qtd + 1,
      peso: a.peso + Number(i.peso_liquido || 0),
      clientes: a.clientes.add(i.cliente_dono || "?"),
    }),
    { qtd: 0, peso: 0, clientes: new Set() }
  );

  return (
    <Layout
      title="Beneficiamento a Terceiros"
      subtitle="Mercadoria recebida para beneficiar por conta de terceiros"
      actions={
        <Link to="/terceiros/nfs/new">
          <Button className="!bg-purple-600 hover:!bg-purple-700" data-testid="new-terceiros-btn"><Plus size={16} /> Nova NF Terceiros</Button>
        </Link>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">NFs registradas</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{totais.qtd}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Clientes ativos</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{totais.clientes.size}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Peso total</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">{fmtKg(totais.peso)}</div>
        </Card>
        <Card className="p-4 border-l-4 border-purple-500">
          <div className="text-xs uppercase tracking-wider text-purple-700 font-bold">Em beneficiamento</div>
          <div className="font-mono text-2xl font-bold text-ink-900 mt-1">
            {items.filter((i) => i.status === "em_beneficiamento").length}
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Cliente, NF, motorista..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="terceiros-search"
              />
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Status</div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md bg-stone-50 border border-stone-300 px-3 py-2 text-sm"
              data-testid="terceiros-status-filter"
            >
              <option value="">Todos</option>
              <option value="bruto">Aguardando</option>
              <option value="em_beneficiamento">Em beneficiamento</option>
              <option value="beneficiada">Finalizada</option>
            </select>
          </div>
          <Button onClick={load} className="!bg-purple-600 hover:!bg-purple-700" data-testid="terceiros-buscar">
            <Search size={14} /> Buscar
          </Button>
        </div>
      </Card>

      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-3">{err}</div>}

      {/* Lista */}
      <Card>
        {loading ? (
          <div className="p-8 text-center"><Spinner size={20} /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-ink-500">
            <Users size={32} className="mx-auto mb-2 text-ink-300" />
            <div>Nenhuma NF de terceiros ainda.</div>
            <Link to="/terceiros/nfs/new" className="text-purple-600 hover:underline text-sm mt-2 inline-block">Registrar primeira NF →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200">
                <tr className="text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3 text-left">NF</th>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-left">Unidade</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-right">Peso Líq.</th>
                  <th className="px-4 py-3 text-right">Serviço/sc</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className="border-b border-ink-200 hover:bg-purple-50/40 cursor-pointer" onClick={() => nav(`/invoices/${i.id}`)}>
                    <td className="px-4 py-3 font-mono">#{i.nota_numero}</td>
                    <td className="px-4 py-3 font-semibold text-purple-900">{i.cliente_dono || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDate(i.data_entrada)}</td>
                    <td className="px-4 py-3 text-xs">{i.unidade}</td>
                    <td className="px-4 py-3 text-xs capitalize">{i.tipo_cebola}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtKg(i.peso_liquido)}</td>
                    <td className="px-4 py-3 text-right font-mono">R$ {Number(i.preco_servico_sc || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[i.status] || "neutral"}>{STATUS_LABEL[i.status] || i.status}</Badge>
                      {i.usa_sacaria_propria && (
                        <div className="text-[10px] text-purple-700 font-semibold mt-0.5 flex items-center gap-0.5">
                          <Package size={10} /> Sacaria própria
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        {i.status === "beneficiada" && (
                          <a
                            href={`/terceiros/nfs/${i.id}/romaneio`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-purple-700 text-xs font-semibold hover:underline inline-flex items-center gap-1"
                            data-testid={`romaneio-btn-${i.id}`}
                          >
                            🖨 Romaneio
                          </a>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); nav(`/invoices/${i.id}`); }} className="text-purple-600 text-xs font-semibold hover:underline" data-testid={`terceiros-row-${i.id}`}>
                          Abrir →
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Layout>
  );
}
